/**
 * **快捷键用到的三个动作** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 *   `targetNodesForToggle`  Ctrl+H 要作用的节点：优先选中的，其次活动节点
 *   `toggleHideUnused`      隐藏 / 显示未连接的端口（Blender 的 Ctrl+H）
 *   `closePanels`           关掉所有浮层（活动节点 + 端口浮层）
 *
 * 这三个同时被**快捷键注册表**和**菜单**用到，所以单独成一块 ——
 * 谁需要谁注入，比留在宿主里到处引用清楚。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 */
import { nextTick } from 'vue'
import type { GraphNodeModel } from '@/types/node'
import type { FlowStoreApi } from '@/graph/flowStore'

export interface ShortcutActionsCtx {
  selectedIds: { value: string[] }
  activeNodeId: { value: string | null }
  socketTip: { value: unknown }
  flowStore: { value: FlowStoreApi | null }
  modelOf: (id: string) => GraphNodeModel | undefined
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string) => void
}

export function useShortcutActions(ctx: ShortcutActionsCtx) {
  const { selectedIds, activeNodeId, socketTip, flowStore, modelOf, commit, showNotice } = ctx

/** Ctrl+H 要作用的节点：优先选中的，其次活动节点 */
function targetNodesForToggle(): string[] {
  if (selectedIds.value.length > 0) return selectedIds.value
  return activeNodeId.value ? [activeNodeId.value] : []
}

/**
 * 隐藏 / 显示未连接的端口（Blender 的 Ctrl+H）。
 *
 * 作用在**选中的节点**上；没选中就用活动节点。这是 Blender 的语义 ——
 * 它的 Ctrl+H 也是「Toggle Hidden Node Sockets」作用于选中节点。
 */
function toggleHideUnused() {
  const targets = targetNodesForToggle()
  if (targets.length === 0) return

  commit('隐藏未连接端口')
  const models = targets.map((id) => modelOf(id)).filter(Boolean) as GraphNodeModel[]
  // 以第一个为准取反，其余跟随 —— 混选时行为可预期
  const next = !models[0]?.hideUnused
  for (const model of models) model.hideUnused = next

  nextTick(() => flowStore.value?.updateNodeInternals(targets))
  showNotice(next ? '已隐藏未连接的端口' : '已显示全部端口')
}

function closePanels() {
  activeNodeId.value = null
  socketTip.value = null
}


  return { targetNodesForToggle, toggleHideUnused, closePanels }
}
